import {
    interventionsScheduleListUrl,
    interventionsScheduleDetailUrl,
    interventionsScheduleReorderUrl,
} from "@/services/api";
import { InterventionSchedule } from "@/types/planning";
import { defineStore } from "pinia";
import axios from "axios";
import { ICostumer } from "@/types/costumer";
import { useCostumersStore } from "./costumers";
import { IWeekModel } from "@/types/week";

export const usePlanningStore = defineStore("planning", {
    state: () => ({
        loading: false as boolean,
        schedules: [] as (InterventionSchedule & { costumer: ICostumer })[],
    }),
    persist: true,
    getters: {
        isLoading: (state) => state.loading,
        getSchedules: (state) => state.schedules,
        getScheduleById: (state) => (id: number) => state.schedules.find((schedule) => schedule.id === id),
        getSchedulesByCustomerId: (state) => (customerId: number) =>
            state.schedules.filter((schedule) => schedule.customer === customerId),
        getSchedulesByContractId: (state) => (contractId: number) =>
            state.schedules.filter((schedule) => schedule.contract === contractId),
        getSchedulesbyWeek: (state) => (week: IWeekModel) => {
            return state.schedules.filter((schedule) => {
                return schedule.scheduled_week === week.week;
            });
        },
    },
    actions: {
        async loadSchedules(weekDate?: string, without_loading: boolean = false) {
            if (!without_loading) {
                this.loading = true;
            }
            const byWeekDate = weekDate ? { week: weekDate } : {};
            try {
                const response = await axios.get(interventionsScheduleListUrl, { params: byWeekDate });
                const costumerStore = useCostumersStore();
                if (response.status === 200) {
                    this.schedules = response.data.map((schedule: InterventionSchedule) => ({
                        ...schedule,
                        costumer: costumerStore.getCustomerById(schedule.customer),
                    }));
                }
            } catch (error) {
                console.error("Failed to load schedules:", error);
            } finally {
                if (!without_loading) {
                    this.loading = false;
                }
            }
            return this.schedules;
        },
        async createSchedule(schedule: InterventionSchedule) {
            this.loading = true;
            let response = null;
            try {
                const { id, ...scheduleWithoutId } = schedule;
                response = await axios.post(interventionsScheduleListUrl, scheduleWithoutId);
                if (response.status === 201) {
                    this.schedules.push(response.data);
                }
            } catch (error) {
                console.error("Failed to create schedule:", error);
            } finally {
                this.loading = false;
            }
            return response;
        },
        async updateSchedule(schedule: InterventionSchedule) {
            this.loading = true;
            let response = null;
            try {
                response = await axios.put(interventionsScheduleDetailUrl(schedule.id), schedule);
                if (response.status === 200) {
                    const index = this.schedules.findIndex((s) => s.id === schedule.id);
                    if (index !== -1) {
                        this.schedules[index] = response.data;
                    }
                }
            } catch (error) {
                console.error("Failed to update schedule:", error);
            } finally {
                this.loading = false;
            }
            return response;
        },
        async deleteSchedule(scheduleId: number) {
            this.loading = true;
            let response = null;
            try {
                response = await axios.delete(interventionsScheduleDetailUrl(scheduleId));
                if (response.status === 204) {
                    this.schedules = this.schedules.filter((schedule) => schedule.id !== scheduleId);
                }
            } catch (error) {
                console.error("Failed to delete schedule:", error);
            } finally {
                this.loading = false;
            }
            return response;
        },
        async reorderSchedule(week: IWeekModel, date: string, from: number, to?: number) {
            let response = null;
            this.loading = true;
            try {
                response = await axios.put(interventionsScheduleReorderUrl, {
                    week: week.week,
                    date: date,
                    ...(from && to ? { from_order: from, to_order: to } : { from_order: from }),
                });
                if (response.status === 200) {
                    await this.loadSchedules(week.week, true); // Reload schedules for the week without setting loading state
                }
            } catch (error) {
                console.error("Failed to delete schedule:", error);
            } finally {
                this.loading = false;
            }
            return response;
        },
    },
});
